import React from "react"
import { Icon } from "@kit/design/icon"
import Badge from "@kit/design/badge"
import { Link } from "core/link"
import { MenuLink, MenuItem } from "@reach/menu-button"
import { PaidFeatureBadge } from "./paid-feature-badge"
import { analytics } from "utils/analytics"
import { slugify } from "core/utils/slugify"

export const NavLink = ({
  href,
  icon,
  iconColor,
  label,
  content,
  section,
  isPaidFeature = false,
  canUseFeature = true,
  userOnTrial = false,
  newTab = false,
  showBetaBadge = false,
  onClick
}) => {
  const ICON_STYLES = "mr-4 overflow-visible"
  const LINK_STYLES =
    "h-full flex p-2 rounded hover:no-underline hover:text-gray-900 focus:outline-blue focus:no-underline focus:bg-gray-50"
  const showPaidBadge = isPaidFeature && (!canUseFeature || userOnTrial)

  function trackClick() {
    analytics.track(
      "Element",
      "Clicked",
      {
        element_type: "link",
        name: `nav_${slugify(label)}`,
        text: label,
        location: "primary_nav",
        section
      },
      { transport: "beacon" }
    )
  }

  function handleMenuKeyDown(e) {
    const menu = e.currentTarget.closest("[data-reach-menu-items]")
    if (!menu) return
    const items = [...menu.querySelectorAll("[role=menuitem]")]
    if (items.length === 0) return
    const currentIndex = items.indexOf(e.currentTarget)
    if (currentIndex === -1) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      const next = items[currentIndex + 1] ?? items[0]
      next.focus()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      const prev = items[currentIndex - 1] ?? items[items.length - 1]
      prev.focus()
    } else if (e.key === "Home") {
      e.preventDefault()
      items[0].focus()
    } else if (e.key === "End") {
      e.preventDefault()
      items[items.length - 1].focus()
    }
  }

  const linkContent = (
    <>
      <Icon name={icon} className={`${ICON_STYLES} ${iconColor}`} />
      <div className="leading-6">
        <div className="font-semibold text-base flex items-center text-gray-900">
          {label}
          {showPaidBadge && <PaidFeatureBadge className="ml-2 shrink-0" />}
          {showBetaBadge && (
            <Badge color="purple" variant="light" className="ml-2">
              Beta
            </Badge>
          )}
        </div>
        <p className="mt-2 text-gray-800">{content}</p>
      </div>
    </>
  )

  if (onClick) {
    return (
      <MenuItem
        className={`main-link ${LINK_STYLES}`}
        onSelect={() => {
          trackClick()
          onClick()
        }}
        onKeyDown={handleMenuKeyDown}
      >
        {linkContent}
      </MenuItem>
    )
  }

  return (
    <MenuLink
      as={Link}
      href={href}
      className={`main-link ${LINK_STYLES}`}
      target={newTab ? "_blank" : undefined}
      onClick={trackClick}
      onKeyDown={handleMenuKeyDown}
    >
      {linkContent}
    </MenuLink>
  )
}
